import { useEffect, useRef } from "react";
import type { MasterClass } from "../../api/types";
import css from "./MkPicker.module.css";

/**
 * Master-class picker for the ДОП cells in row 3. Per screen-03 эталон
 * (`.cyber-pop` with `.cyber-opt` rows): green/purple gradient border, dark
 * inner gradient, list of MK options with hover chevron `›`. Includes a
 * leading "Без МК" row that clears the cell.
 *
 * Width follows the anchor (ДОП cell is ~1/3 of the mk-trio strip) but the
 * popover stretches wider so option names fit.
 */
export interface MkPickerProps {
  /** Currently selected master_class id (or "" if none). */
  value: string;
  /** All active master_classes for the current branch. */
  options: MasterClass[];
  /** Which side of the trigger to anchor — prevents the popover from
     escaping the right edge of the modal sheet for the rightmost mk-cell. */
  align?: "left" | "center" | "right";
  onPick: (mcId: string) => void;
  onClose: () => void;
}

export function MkPicker({ value, options, align = "left", onPick, onClose }: MkPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  /* Leading "Без МК" + active options. */
  const rows: { id: string; name: string }[] = [
    { id: "", name: "Без МК" },
    ...options.filter((o) => o.active).map((o) => ({ id: String(o.id), name: o.name })),
  ];

  const alignClass = align === "right" ? css.alignRight : align === "center" ? css.alignCenter : css.alignLeft;

  return (
    <div ref={rootRef} className={`${css.pop} ${alignClass}`} role="dialog" aria-label="Выбор мастер-класса">
      <div className={css.list}>
        {rows.map((opt) => {
          const selected = opt.id === value;
          return (
            <button
              key={opt.id || "_none_"}
              type="button"
              className={`${css.opt} ${selected ? css.selected : ""} ${!opt.id ? css.clearOpt : ""}`}
              onClick={() => {
                onPick(opt.id);
                onClose();
              }}
            >
              <span className={css.chev} aria-hidden="true">›</span>
              <span className={css.optName}>{opt.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
